import React, { useState, useEffect, useRef } from 'react';
import { Clock, Calendar, User, CheckCircle, XCircle, FileText, AlertCircle, Package, DollarSign, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import { getPayrollRequestsForBusiness, updatePayrollRequestStatus } from '../../lib/payrollRequests';
import { updatePayrollEntry } from '../../lib/payroll';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface PayrollRequest {
  id: string;
  business_id: string;
  resource_id: string;
  payroll_entry_id: string | null;
  request_type: 'change' | 'add' | 'add_flat_rate';
  
  // Original entry data
  original_clock_in?: string;
  original_clock_out?: string;
  original_break_start?: string;
  original_break_end?: string;
  original_total_minutes?: number;
  original_total_earnings?: number;
  original_notes?: string;
  
  // Requested changes
  requested_clock_in?: string;
  requested_clock_out?: string;
  requested_break_start?: string;
  requested_break_end?: string;
  requested_total_minutes?: number;
  requested_total_earnings?: number;
  requested_notes?: string;
  
  // For new entry requests
  requested_work_date?: string;
  requested_booking_id?: string;
  
  // For flat rate requests
  requested_flat_rate_item_id?: string;
  requested_flat_rate_hours?: number;
  requested_flat_rate_completed_at?: string;
  
  // Request metadata
  request_reason?: string;
  status: 'pending' | 'approved' | 'denied';
  requested_at: string;
  reviewed_at?: string;
  reviewed_by?: string;
  reviewer_notes?: string;
  
  created_at: string;
  updated_at: string;
  
  // Related data
  resource?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  payroll_entry?: {
    id: string;
    clock_in?: string;
    clock_out?: string;
    total_minutes?: number;
    total_earnings?: number;
  };
  booking?: {
    id: string;
    booking_number: number;
    title: string;
  };
  flat_rate_item?: {
    id: string;
    name: string;
    description?: string;
  };
}

interface PayrollRequestsSectionProps {
  businessId: string;
  onRequestProcessed?: () => void;
}

// Helper function to format duration
const formatDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

export default function PayrollRequestsSection({ businessId, onRequestProcessed }: PayrollRequestsSectionProps) {
  const [requests, setRequests] = useState<PayrollRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingRequest, setProcessingRequest] = useState<string | null>(null);
  const [expandedRequests, setExpandedRequests] = useState<Set<string>>(new Set());
  const [activeFilter, setActiveFilter] = useState<'pending' | 'approved' | 'denied'>('pending');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadRequests();
  }, [businessId, activeFilter]);

  // Handle click outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadRequests = async () => {
    if (!businessId) return;

    try {
      setLoading(true);
      const data = await getPayrollRequestsForBusiness(businessId, activeFilter);
      setRequests(data);
    } catch (error) {
      console.error('Error loading payroll requests:', error);
      toast.error('Failed to load payroll requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (request: PayrollRequest) => {
    try {
      setProcessingRequest(request.id);
      
      // Update the request status to approved
      await updatePayrollRequestStatus(request.id, 'approved');
      
      if (request.request_type === 'add_flat_rate') {
        // Handle flat rate requests
        if (!supabase) {
          throw new Error('Supabase client not initialized');
        }
        
        if (!request.requested_flat_rate_item_id || !request.requested_booking_id) {
          throw new Error('Missing flat rate item or booking ID');
        }

        console.log('Creating flat rate entry with completion timestamp:', {
          booking_id: request.requested_booking_id,
          item_id: request.requested_flat_rate_item_id,
          resource_id: request.resource_id,
          hours: request.requested_flat_rate_hours,
          completed_at: request.requested_flat_rate_completed_at
        });
        
        // Get current authenticated user for completed_by field
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError || !user) {
          throw new Error('User not authenticated');
        }

        // Create the booking flat rate entry with completion timestamp
        const { data: flatRateEntry, error: flatRateError } = await supabase
          .from('booking_flat_rates')
          .insert({
            booking_id: request.requested_booking_id,
            item_id: request.requested_flat_rate_item_id,
            resource_id: request.resource_id,
            hours: request.requested_flat_rate_hours,
            completed_at: request.requested_flat_rate_completed_at || new Date().toISOString(),
            completed_by: user.id, // Use authenticated user (manager) not resource
            notes: request.request_reason || null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select()
          .single();

        if (flatRateError) {
          console.error('Error creating flat rate entry:', flatRateError);
          throw flatRateError;
        }

        console.log('Created flat rate entry:', flatRateEntry);

        // Fetch additional details needed for the payroll entry
        const { data: detailsData, error: detailsError } = await supabase
          .from('booking_flat_rates')
          .select(`
            *,
            bookings!inner (
              business_id,
              booking_number,
              title
            ),
            flat_rate_items!inner (
              name,
              description
            ),
            resources!inner (
              compensation_type
            )
          `)
          .eq('id', flatRateEntry.id)
          .single();

        if (detailsError) {
          console.error('Error fetching details for payroll entry:', detailsError);
          throw detailsError;
        }

        // Create the corresponding payroll entry using the same logic as mark_flat_rate_items_completed
        const { error: payrollError } = await supabase
          .from('payroll_page')
          .insert({
            business_id: detailsData.bookings.business_id,
            resource_id: request.resource_id,
            booking_id: request.requested_booking_id,
            flat_rate_id: request.requested_flat_rate_item_id,
            hours: request.requested_flat_rate_hours,
            entry_type: 'flat_rate',
            compensation_type: detailsData.resources.compensation_type,
            process_status: 'UNPAID',
            item_name: detailsData.flat_rate_items.name,
            item_description: detailsData.flat_rate_items.description,
            booking_number: detailsData.bookings.booking_number,
            booking_title: detailsData.bookings.title,
            completed_at: request.requested_flat_rate_completed_at || new Date().toISOString(),
            created_at: request.requested_flat_rate_completed_at || new Date().toISOString()
          });

        if (payrollError) {
          console.error('Error creating payroll entry:', payrollError);
          throw payrollError;
        }

        toast.success(`Approved flat rate request from ${request.resource?.first_name} ${request.resource?.last_name}`);
      } else {
        // Handle regular change/add requests
        if (!request.payroll_entry_id) {
          throw new Error('Missing payroll entry ID for regular request');
        }

        const updates: any = {
          clock_in: request.requested_clock_in,
          clock_out: request.requested_clock_out,
          break_start: request.requested_break_start,
          break_end: request.requested_break_end,
          total_minutes: request.requested_total_minutes,
          total_earnings: request.requested_total_earnings,
          notes: request.requested_notes,
          updated_at: new Date().toISOString()
        };

        await updatePayrollEntry(request.payroll_entry_id, updates);
        
        toast.success(`Approved change request from ${request.resource?.first_name} ${request.resource?.last_name}`);
      }
      
      // Reload requests and notify parent
      await loadRequests();
      onRequestProcessed?.();
      
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error('Failed to approve request');
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleDeny = async (request: PayrollRequest, reason?: string) => {
    try {
      setProcessingRequest(request.id);
      
      await updatePayrollRequestStatus(request.id, 'denied', reason);
      
      toast.success(`Denied change request from ${request.resource?.first_name} ${request.resource?.last_name}`);
      
      // Reload requests
      await loadRequests();
      
    } catch (error) {
      console.error('Error denying request:', error);
      toast.error('Failed to deny change request');
    } finally {
      setProcessingRequest(null);
    }
  };

  const toggleExpanded = (requestId: string) => {
    setExpandedRequests(prev => {
      const next = new Set(prev);
      if (next.has(requestId)) {
        next.delete(requestId);
      } else {
        next.add(requestId);
      }
      return next;
    });
  };

  const getTimeDifference = (original: number, requested: number) => {
    const difference = requested - original;
    if (difference === 0) return null;
    
    const hours = Math.floor(Math.abs(difference) / 60);
    const minutes = Math.abs(difference) % 60;
    const sign = difference > 0 ? '+' : '-';
    
    return { 
      text: `${sign}${hours}h ${minutes}m`,
      isIncrease: difference > 0
    };
  };

  const getRequestTypeLabel = (requestType: string) => {
    switch (requestType) {
      case 'change': return 'Time Change';
      case 'add': return 'Add Entry';
      case 'add_flat_rate': return 'Add Flat Rate';
      default: return 'Request';
    }
  };

  const getRequestTypeBadge = (requestType: string) => {
    switch (requestType) {
      case 'change': return 'bg-blue-100 text-blue-700';
      case 'add': return 'bg-green-100 text-green-700';
      case 'add_flat_rate': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Since we now load only the filtered requests, use requests directly
  const filteredRequests = requests;
  
  // For count display, we show the current loaded count
  // Note: This only shows count for the currently loaded filter
  const currentCount = requests.length;

  if (loading) {
    return (
      <div className="p-4 sm:p-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          <span className="ml-3 text-gray-600">Loading payroll requests...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="px-4 sm:px-8 py-4 sm:py-6 border-b border-white/20 bg-gradient-to-r from-purple-50/30 to-indigo-50/30">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 rounded-xl">
                <FileText className="h-5 w-5 text-purple-600" />
              </div>
              Payroll Requests
              {activeFilter === 'pending' && currentCount > 0 && (
                <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-medium text-white bg-red-500 rounded-full">
                  {currentCount > 99 ? '99+' : currentCount}
                </span>
              )}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Review and approve employee payroll requests
            </p>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            {/* Filter Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 bg-white/60 backdrop-blur-sm border border-white/20 rounded-xl hover:bg-white/80 transition-all duration-200 text-sm font-medium text-gray-700 min-w-[140px] justify-between"
              >
                <span className="flex items-center gap-2">
                  {activeFilter === 'pending' && (
                    <>
                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                      Pending
                    </>
                  )}
                  {activeFilter === 'approved' && (
                    <>
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      Approved
                    </>
                  )}
                  {activeFilter === 'denied' && (
                    <>
                      <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      Denied
                    </>
                  )}
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-full bg-white/95 backdrop-blur-sm border border-white/20 rounded-xl shadow-lg z-10">
                  <button
                    onClick={() => {
                      setActiveFilter('pending');
                      setIsDropdownOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-gray-50/50 transition-colors ${
                      activeFilter === 'pending' ? 'bg-orange-50/50 text-orange-700' : 'text-gray-700'
                    } rounded-t-xl`}
                  >
                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                    <span className="flex-1 text-left">Pending</span>
                    <span className="text-xs text-gray-500">
                      ({activeFilter === 'pending' ? currentCount : '...'})
                    </span>
                  </button>
                  <button
                    onClick={() => {
                      setActiveFilter('approved');
                      setIsDropdownOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-gray-50/50 transition-colors ${
                      activeFilter === 'approved' ? 'bg-green-50/50 text-green-700' : 'text-gray-700'
                    }`}
                  >
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="flex-1 text-left">Approved</span>
                    <span className="text-xs text-gray-500">
                      ({activeFilter === 'approved' ? currentCount : '...'})
                    </span>
                  </button>
                  <button
                    onClick={() => {
                      setActiveFilter('denied');
                      setIsDropdownOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-gray-50/50 transition-colors ${
                      activeFilter === 'denied' ? 'bg-red-50/50 text-red-700' : 'text-gray-700'
                    } rounded-b-xl`}
                  >
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <span className="flex-1 text-left">Denied</span>
                    <span className="text-xs text-gray-500">
                      ({activeFilter === 'denied' ? currentCount : '...'})
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 sm:p-8">
        {filteredRequests.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No {activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1)} Requests
            </h3>
            <p className="text-gray-500">
              {activeFilter === 'pending' 
                ? 'No pending payroll requests to review.'
                : activeFilter === 'approved'
                ? 'No approved payroll requests.'
                : 'No denied payroll requests.'}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Filtered Requests */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                {activeFilter === 'pending' && <AlertCircle className="h-5 w-5 text-orange-500" />}
                {activeFilter === 'approved' && <CheckCircle className="h-5 w-5 text-green-500" />}
                {activeFilter === 'denied' && <XCircle className="h-5 w-5 text-red-500" />}
                {activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1)} Requests ({filteredRequests.length})
              </h3>
              <div className="space-y-4">
                {filteredRequests.map((request) => {
                    const isExpanded = expandedRequests.has(request.id);
                    const timeDiff = request.request_type === 'change' ? getTimeDifference(
                      request.original_total_minutes || 0,
                      request.requested_total_minutes || 0
                    ) : null;

                    return (
                      <div key={request.id} className="bg-white/60 backdrop-blur-sm border border-white/20 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300">
                        {/* Request Header */}
                        <div 
                          className="p-4 cursor-pointer hover:bg-white/40 transition-all duration-300"
                          onClick={() => toggleExpanded(request.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-semibold shadow-lg">
                                {request.resource?.first_name?.[0]}{request.resource?.last_name?.[0]}
                              </div>
                              <div>
                                <h4 className="font-semibold text-gray-900">
                                  {request.resource?.first_name} {request.resource?.last_name}
                                </h4>
                                <p className="text-sm text-gray-600">
                                  {format(new Date(request.requested_at), 'MMM d, yyyy h:mm a')}
                                </p>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRequestTypeBadge(request.request_type)}`}>
                                {getRequestTypeLabel(request.request_type)}
                              </span>
                              {timeDiff && (
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  timeDiff.isIncrease ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                }`}>
                                  {timeDiff.text}
                                </span>
                              )}
                              <span className={`px-2 py-1 rounded-full text-xs font-medium backdrop-blur-sm ${
                                request.status === 'pending' 
                                  ? 'bg-orange-100/70 text-orange-700'
                                  : request.status === 'approved'
                                  ? 'bg-green-100/70 text-green-700'
                                  : 'bg-red-100/70 text-red-700'
                              }`}>
                                {request.status.toUpperCase()}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Expanded Details */}
                        {isExpanded && (
                          <div className="border-t border-white/20 p-4 bg-gradient-to-r from-white/30 to-purple-50/20">
                            {request.request_type === 'add_flat_rate' ? (
                              /* Flat Rate Request Details */
                              <>
                                <div className="mb-6">
                                  <div className="bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                                    <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                      <Package className="h-4 w-4 text-purple-600" />
                                      Requested Flat Rate Item
                                    </h5>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Job:</span>
                                        <span className="font-medium">
                                          {request.booking ? `#${request.booking.booking_number} - ${request.booking.title}` : request.requested_booking_id || '-'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Flat Rate Item:</span>
                                        <span className="font-medium">
                                          {request.flat_rate_item ? request.flat_rate_item.name : request.requested_flat_rate_item_id || '-'}
                                        </span>
                                      </div>
                                      {request.flat_rate_item?.description && (
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Description:</span>
                                          <span className="font-medium text-gray-700">{request.flat_rate_item.description}</span>
                                        </div>
                                      )}
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Hours:</span>
                                        <span className="font-medium">{request.requested_flat_rate_hours || 0} hours</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Will be marked completed on:</span>
                                        <span className="font-medium text-purple-700">
                                          {request.requested_flat_rate_completed_at 
                                            ? (
                                              <div className="text-right">
                                                <div>{format(new Date(request.requested_flat_rate_completed_at), 'MMM d, yyyy h:mm a')}</div>
                                                <div className="text-xs text-purple-600 mt-1">
                                                  (Inherited from existing group)
                                                </div>
                                              </div>
                                            )
                                            : (
                                              <div className="text-right">
                                                <div>Current date/time when approved</div>
                                                <div className="text-xs text-orange-600 mt-1">
                                                  (No existing group to inherit from)
                                                </div>
                                              </div>
                                            )
                                          }
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : request.request_type === 'add' ? (
                              /* Add Entry Request Details */
                              <>
                                <div className="mb-6">
                                  <div className="bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                                    <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                      <Calendar className="h-4 w-4 text-green-600" />
                                      Requested New Entry
                                    </h5>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Work Date:</span>
                                        <span className="font-medium">
                                          {request.requested_work_date ? format(new Date(request.requested_work_date), 'MMM d, yyyy') : '-'}
                                        </span>
                                      </div>
                                      {request.booking && (
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Job:</span>
                                          <span className="font-medium">
                                            #{request.booking.booking_number} - {request.booking.title}
                                          </span>
                                        </div>
                                      )}
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Clock In:</span>
                                        <span className="font-medium">
                                          {request.requested_clock_in ? format(new Date(request.requested_clock_in), 'h:mm a') : '-'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Clock Out:</span>
                                        <span className="font-medium">
                                          {request.requested_clock_out ? format(new Date(request.requested_clock_out), 'h:mm a') : '-'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Total Time:</span>
                                        <span className="font-medium">{formatDuration(request.requested_total_minutes || 0)}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Estimated Earnings:</span>
                                        <span className="font-medium">${(request.requested_total_earnings || 0).toFixed(2)}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              /* Change Request Details */
                              <>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                                  {/* Original Entry */}
                                  <div className="bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                                    <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                      <Clock className="h-4 w-4 text-gray-600" />
                                      Original Entry
                                    </h5>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Clock In:</span>
                                        <span className="font-medium">
                                          {request.original_clock_in ? format(new Date(request.original_clock_in), 'h:mm a') : '-'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Clock Out:</span>
                                        <span className="font-medium">
                                          {request.original_clock_out ? format(new Date(request.original_clock_out), 'h:mm a') : '-'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Total Time:</span>
                                        <span className="font-medium">{formatDuration(request.original_total_minutes || 0)}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Earnings:</span>
                                        <span className="font-medium">${(request.original_total_earnings || 0).toFixed(2)}</span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Requested Changes */}
                                  <div className="bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                                    <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                      <Calendar className="h-4 w-4 text-blue-600" />
                                      Requested Changes
                                    </h5>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Clock In:</span>
                                        <span className="font-medium">
                                          {request.requested_clock_in ? format(new Date(request.requested_clock_in), 'h:mm a') : '-'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Clock Out:</span>
                                        <span className="font-medium">
                                          {request.requested_clock_out ? format(new Date(request.requested_clock_out), 'h:mm a') : '-'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Total Time:</span>
                                        <span className="font-medium">{formatDuration(request.requested_total_minutes || 0)}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Earnings:</span>
                                        <span className="font-medium">${(request.requested_total_earnings || 0).toFixed(2)}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            )}

                            {/* Request Reason */}
                            {request.request_reason && (
                              <div className="mb-6">
                                <h5 className="font-semibold text-gray-900 mb-2">
                                  {request.request_type === 'add_flat_rate' ? 'Reason for Additional Items:' : 
                                   request.request_type === 'add' ? 'Reason for Missing Entry:' : 
                                   'Reason for Change:'}
                                </h5>
                                <p className="text-gray-700 bg-white/70 backdrop-blur-sm rounded-lg p-3 italic border border-white/20">
                                  "{request.request_reason}"
                                </p>
                              </div>
                            )}

                            {/* Action Buttons (only for pending requests) */}
                            {request.status === 'pending' ? (
                              <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleApprove(request);
                                  }}
                                  disabled={processingRequest === request.id}
                                  className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                                >
                                  {processingRequest === request.id ? (
                                    <div className="h-4 w-4 border border-white border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <CheckCircle className="h-4 w-4" />
                                  )}
                                  {request.request_type === 'add_flat_rate' ? 'Approve Flat Rate' : 
                                   request.request_type === 'add' ? 'Approve Entry' : 
                                   'Approve Changes'}
                                </button>
                                
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeny(request);
                                  }}
                                  disabled={processingRequest === request.id}
                                  className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-xl hover:from-red-700 hover:to-rose-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                                >
                                  <XCircle className="h-4 w-4" />
                                  Deny Request
                                </button>
                              </div>
                            ) : (
                              /* Review Information for processed requests */
                              <div className="bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    {request.status === 'approved' ? (
                                      <CheckCircle className="h-5 w-5 text-green-600" />
                                    ) : (
                                      <XCircle className="h-5 w-5 text-red-600" />
                                    )}
                                    <div>
                                      <h6 className="font-semibold text-gray-900">
                                        {request.status === 'approved' ? 'Request Approved' : 'Request Denied'}
                                      </h6>
                                      <p className="text-sm text-gray-600">
                                        {request.reviewed_at 
                                          ? `Reviewed on ${format(new Date(request.reviewed_at), 'MMM d, yyyy h:mm a')}`
                                          : `Processed on ${format(new Date(request.requested_at), 'MMM d, yyyy h:mm a')}`
                                        }
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                {request.reviewer_notes && (
                                  <div className="mt-3 pt-3 border-t border-white/20">
                                    <p className="text-sm text-gray-700">
                                      <span className="font-medium">Reviewer Note:</span> "{request.reviewer_notes}"
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
          </div>
        )}
      </div>
    </>
  );
}